import { ChevronRight, FileUser, Mail } from 'lucide-react';

import { usePilotAgentEnteredIntake } from '@/shared/hooks/use-active-pilot-program';

export type QuickPlanMode = 'quick-plan' | 'intake-form';

interface QuickPlanModeSelectorProps {
  onSelectMode: (mode: QuickPlanMode) => void;
}

const OPTIONS = [
  {
    mode: 'quick-plan' as const,
    icon: <FileUser className="size-5" />,
    title: 'No intake form',
    description: 'Enter basic info to generate a plan instantly. No email invite needed.',
  },
  {
    mode: 'intake-form' as const,
    icon: <Mail className="size-5" />,
    title: 'Send medical intake form',
    description: 'Send a medical questionnaire to your client to collect health and care details.',
  },
];

export function QuickPlanModeSelector({ onSelectMode }: QuickPlanModeSelectorProps) {
  // "No email invite needed" stops being true once the client has to give permission first,
  // and this is the screen where the advisor picks between the two paths — so it has to say so
  // before they commit, not after.
  const isAgentEntered = !!usePilotAgentEnteredIntake();

  const options = OPTIONS.map(option =>
    option.mode === 'quick-plan' && isAgentEntered
      ? {
          ...option,
          description: 'Enter basic info to generate a plan. Your client confirms by email first.',
        }
      : option,
  );

  return (
    <div className="flex flex-col gap-3">
      {options.map(({ mode, icon, title, description }) => (
        <button
          key={mode}
          type="button"
          onClick={() => onSelectMode(mode)}
          className="hover:border-primary hover:bg-primary/5 border-border bg-background group flex w-full cursor-pointer items-center gap-4 rounded-lg border p-4 text-left transition-all"
        >
          <div className="group-hover:bg-primary text-foreground bg-muted group-hover:text-background flex size-12 shrink-0 items-center justify-center rounded-full transition-all">
            {icon}
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-950">{title}</h3>
            <p className="text-foreground text-sm">{description}</p>
          </div>
          <ChevronRight className="group-hover:text-primary text-muted-foreground size-5 transition-all" />
        </button>
      ))}
    </div>
  );
}
